import type { ReportMeta } from '@diagon-alley/common';
import { Collapse, Code, Divider, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { CaretDown, CaretRight } from '@phosphor-icons/react';

import styles from './RecipeRow.module.css';

type Recipe = NonNullable<ReportMeta['recipe']>;

interface RecipeRowProps {
    recipe: Recipe;
}

/**
 * Collapsible "Show recipe" affordance for freeform (Slice 8.1) and
 * later for any report that wants its provenance visible. Renders below
 * the result table and exposes the exact service / method / params /
 * capabilities the agent used so the user can sanity-check the call
 * shape and audit dynamic reports.
 *
 * Collapsed by default — the badge in the title strip is enough signal
 * for the typical case; expanding is for users who want to verify or
 * copy the spec.
 */
export default function RecipeRow({ recipe }: RecipeRowProps) {
    const [opened, { toggle }] = useDisclosure(false);

    const primaryCapKeys = recipe.capabilities
        ? Object.keys(recipe.capabilities)
        : [];
    const isChained = !!(recipe.secondary && recipe.join);

    return (
        <div className={styles.row}>
            <UnstyledButton
                onClick={toggle}
                className={styles.toggle}
                aria-expanded={opened}
                aria-controls="recipe-body"
            >
                {opened ? (
                    <CaretDown size={12} weight="bold" />
                ) : (
                    <CaretRight size={12} weight="bold" />
                )}
                <span className={styles.toggleLabel}>
                    {opened ? 'Hide' : 'Show'} recipe
                </span>
                <span className={recipe.summary ? styles.summaryPlain : styles.summary}>
                    {summarise(recipe)}
                    {!recipe.summary && primaryCapKeys.length > 0
                        ? ` · ${primaryCapKeys.join(' + ')}`
                        : ''}
                </span>
            </UnstyledButton>
            <Collapse in={opened}>
                {recipe.datasets && recipe.datasets.length > 0 ? (
                    <div className={styles.body} id="recipe-body">
                        {recipe.datasets.map((dataset, i) => (
                            <div key={dataset.name}>
                                {i > 0 && <Divider my={6} />}
                                <div className={styles.sectionLabel}>Dataset · {dataset.name}</div>
                                <RecipeField label="Method" value={`${dataset.serviceName}.${dataset.methodName}`} />
                                <RecipeField label="Params" value={JSON.stringify(dataset.params, null, 2)} block />
                                {dataset.capabilities && Object.keys(dataset.capabilities).length > 0 && (
                                    <RecipeField label="Capabilities" value={JSON.stringify(dataset.capabilities, null, 2)} block />
                                )}
                            </div>
                        ))}
                        <Divider my={6} />
                        <div className={styles.sectionLabel}>Query</div>
                        <RecipeField label="SQL" value={recipe.sql ?? ''} block />
                    </div>
                ) : (
                    <div className={styles.body} id="recipe-body">
                        <div className={styles.sectionLabel}>
                            {isChained ? 'Primary' : 'Source'}
                        </div>
                        <RecipeField label="Service" value={recipe.serviceName} />
                        <RecipeField label="Method" value={recipe.methodName} />
                        <RecipeField
                            label="Params"
                            value={JSON.stringify(recipe.params, null, 2)}
                            block
                        />
                        {primaryCapKeys.length > 0 && (
                            <RecipeField
                                label="Capabilities"
                                value={JSON.stringify(recipe.capabilities, null, 2)}
                                block
                            />
                        )}

                        {isChained && recipe.secondary && recipe.join && (
                            <>
                                <Divider my={6} />
                                <div className={styles.sectionLabel}>Secondary</div>
                                <RecipeField
                                    label="Service"
                                    value={recipe.secondary.serviceName}
                                />
                                <RecipeField
                                    label="Method"
                                    value={recipe.secondary.methodName}
                                />
                                <RecipeField
                                    label="Params"
                                    value={JSON.stringify(
                                        recipe.secondary.params,
                                        null,
                                        2,
                                    )}
                                    block
                                />
                                {recipe.secondary.capabilities &&
                                    Object.keys(recipe.secondary.capabilities)
                                        .length > 0 && (
                                        <RecipeField
                                            label="Capabilities"
                                            value={JSON.stringify(
                                                recipe.secondary.capabilities,
                                                null,
                                                2,
                                            )}
                                            block
                                        />
                                    )}

                                <Divider my={6} />
                                <div className={styles.sectionLabel}>Join</div>
                                <RecipeField
                                    label="Clause"
                                    value={`primary.${recipe.join.leftKey} ↔ secondary.${recipe.join.rightKey} (${recipe.join.type})`}
                                />
                            </>
                        )}
                    </div>
                )}
            </Collapse>
        </div>
    );
}

function summarise(recipe: Recipe): string {
    if (recipe.summary) return recipe.summary;
    if (recipe.datasets && recipe.datasets.length > 0) {
        return `${recipe.datasets.map((d) => `${d.serviceName}.${d.methodName}`).join(' + ')} → SQL`;
    }
    const primary = `${recipe.serviceName}.${recipe.methodName}`;
    if (!recipe.secondary || !recipe.join) return primary;
    const secondary = `${recipe.secondary.serviceName}.${recipe.secondary.methodName}`;
    return `${primary} ⋈ ${secondary} on ${recipe.join.leftKey}`;
}

interface RecipeFieldProps {
    label: string;
    value: string;
    block?: boolean;
}

function RecipeField({ label, value, block }: RecipeFieldProps) {
    return (
        <div className={block ? styles.fieldBlock : styles.field}>
            <span className={styles.fieldLabel}>{label}</span>
            <Code block={block}>{value}</Code>
        </div>
    );
}
